import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Skeleton,
  Fab,
  Tooltip,
  ImageList,
  ImageListItem,
  useMediaQuery,
  useTheme,
  CircularProgress,
  TextField,
  LinearProgress
} from '@mui/material';
import {
  Collections as CollectionsIcon,
  MoreVert as MoreVertIcon,
  LinkOff as LinkOffIcon,
  Visibility as VisibilityIcon,
  Share as ShareIcon,
  Add as AddIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Event as EventIcon,
  TripOrigin as TripOriginIcon,
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  Edit as EditIcon,
  AddPhotoAlternate as AddPhotoAlternateIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAlbumIntegration } from '../../hooks/useAlbumIntegration';
import { useSubscription } from '../../contexts/SubscriptionContext';
import CreateAlbumFromPhotos from './CreateAlbumFromPhotos';

const AlbumCard = styled(Card)(({ theme }) => ({
   height: '100%',
   display: 'flex',
   flexDirection: 'column',
   transition: 'all 0.3s ease-in-out',
   '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[12],
   },
}));

const PhotoCount = styled(Chip)(({ theme }) => ({
   backgroundColor: 'rgba(0, 0, 0, 0.7)',
   color: 'white',
   fontSize: '0.75rem',
}));

const CreateAlbumFab = styled(Fab)(({ theme }) => ({
   position: 'fixed',
   bottom: theme.spacing(3),
   right: theme.spacing(3),
   zIndex: 1000,
}));

interface Album {
   id: string;
   title: string;
   description?: string;
   coverPhotoUrl?: string;
   coverPhotoId?: string;
   photoIds?: string[];
   photoCount: number;
   isPublic: boolean;
   createdAt: any;
   createdBy: string;
   source?: 'trip' | 'event' | 'manual';
   tags?: string[];
   members?: Array<{
      userId: string;
      email: string;
      displayName?: string;
      photoURL?: string;
      role: 'owner' | 'editor' | 'viewer';
      joinedAt?: any;
   }>;
   userRole?: 'owner' | 'editor' | 'viewer';
   tripId?: string;
   eventId?: string;
}

interface Photo {
   id: string;
   url: string;
   thumbnailUrl?: string;
   name: string;
   takenAt?: any;
   size?: number;
   location?: {
      latitude: number;
      longitude: number;
      address?: string;
   };
}

interface LinkedAlbumsProps {
   tripId?: string;
   eventId?: string;
   userId: string;
   userEmail?: string;
   photos?: Photo[];
   title?: string;
   /** When this value changes, albums are refetched (e.g. after adding a photo to an album). */
   refreshTrigger?: number;
}

const LinkedAlbums: React.FC<LinkedAlbumsProps> = ({
   tripId,
   eventId,
   userId,
   userEmail,
   photos = [],
   title,
   refreshTrigger,
}) => {
   const [albums, setAlbums] = useState<Album[]>([]);
   const [loading, setLoading] = useState(true);
   const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
   const [selectedAlbum, setSelectedAlbum] = useState<Album | null>(null);
   const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);
   const [createAlbumOpen, setCreateAlbumOpen] = useState(false);
   const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
   const [albumPhotos, setAlbumPhotos] = useState<Photo[]>([]);
   const [loadingPhotos, setLoadingPhotos] = useState(false);
   const [editDialogOpen, setEditDialogOpen] = useState(false);
   const [editingAlbum, setEditingAlbum] = useState<Album | null>(null);
   const [editTitle, setEditTitle] = useState('');
   const [editDescription, setEditDescription] = useState('');
   const [selectedCoverPhoto, setSelectedCoverPhoto] = useState<string | null>(null);
   const [albumCoverPhotos, setAlbumCoverPhotos] = useState<{ [photoId: string]: any }>({});
   const [addPhotosDialogOpen, setAddPhotosDialogOpen] = useState(false);
   const [addPhotosAlbum, setAddPhotosAlbum] = useState<Album | null>(null);
   const [addPhotosAlbumId, setAddPhotosAlbumId] = useState<string | null>(null);
   const [tripPhotos, setTripPhotos] = useState<Photo[]>([]);
   const [selectedTripPhotos, setSelectedTripPhotos] = useState<Set<string>>(new Set());
   const [pendingUploadFiles, setPendingUploadFiles] = useState<File[]>([]);
   const [pendingPreviewUrls, setPendingPreviewUrls] = useState<string[]>([]);
   const [uploadingPhotos, setUploadingPhotos] = useState(false);
   const [fullImageViewerOpen, setFullImageViewerOpen] = useState(false);
   const [currentImageIndex, setCurrentImageIndex] = useState(0);
   const navigate = useNavigate();
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('md'));

   const {
      createAlbumFromPhotos,
      unlinkAlbum,
      getLinkedAlbums,
      loading: actionLoading,
      error,
   } = useAlbumIntegration({ tripId, eventId, userId });

   useEffect(() => {
      loadAlbums();
   }, [tripId, eventId]);

   useEffect(() => {
      if (refreshTrigger != null && refreshTrigger > 0) {
         loadAlbums();
      }
   }, [refreshTrigger]);

   const loadAlbums = async () => {
      setLoading(true);
      console.log('🔍 LinkedAlbums: Loading albums for:', { tripId, eventId, userId, userEmail });
      try {
         const linkedAlbums = await getLinkedAlbums(userEmail);
         console.log('📦 LinkedAlbums: Received albums:', linkedAlbums);
         setAlbums(
            (linkedAlbums || []).map((a: Album) => ({
               ...a,
               // Prefer API photoCount (counts existing non-deleted photos); fallback to photoIds.length
               photoCount:
                  typeof a.photoCount === 'number'
                     ? a.photoCount
                     : Array.isArray(a.photoIds)
                        ? a.photoIds.length
                        : 0,
            }))
         );

         // Load cover photos for albums that have coverPhotoId
         await loadCoverPhotos(linkedAlbums);
      } catch (error) {
         console.error('❌ LinkedAlbums: Error loading albums:', error);
      } finally {
         setLoading(false);
      }
   };

   const loadCoverPhotos = async (albumList: Album[]) => {
      // Collect only explicitly set cover photo IDs from albums
      const allPhotoIds = albumList.map((album) => album.coverPhotoId).filter(Boolean);

      if (allPhotoIds.length === 0) {
         setAlbumCoverPhotos({});
         return;
      }

      try {
         const { getAuthHeaders } = await import('../../utils/auth');
         const headers = await getAuthHeaders();

         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/albums/photos/batch`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ photoIds: allPhotoIds }),
         });

         if (response.ok) {
            const data = await response.json();

            // Create map where key = photoId, value = Photo object (same as AlbumsListPage)
            const map: { [photoId: string]: any } = {};
            (data.photos || []).forEach((photo: any) => {
               map[photo.id] = photo;
            });

            setAlbumCoverPhotos(map);
         } else {
            console.error('❌ API request failed:', response.status, response.statusText);
            const errorText = await response.text();
            console.error('❌ Error response:', errorText);
            setAlbumCoverPhotos({});
         }
      } catch (error) {
         console.error('💥 Error fetching cover photos:', error);
         setAlbumCoverPhotos({});
      }
   };

   // Helper to get cover photo URL - only use explicitly set coverPhotoId
   const getCoverPhotoUrl = (album: Album) => {
      // Only use explicitly set coverPhotoId, don't fallback to first photo
      const coverId = album.coverPhotoId;
      if (coverId && albumCoverPhotos[coverId]) {
         return albumCoverPhotos[coverId].url;
      }
      return album.coverPhotoUrl || '';
   };

   const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, album: Album) => {
      setAnchorEl(event.currentTarget);
      setSelectedAlbum(album);
   };

   const handleMenuClose = () => {
      setAnchorEl(null);
      setSelectedAlbum(null);
   };

   const handleViewAlbum = () => {
      if (selectedAlbum) {
         navigate(`/albums/${selectedAlbum.id}`);
      }
      handleMenuClose();
   };

   const handleUnlinkAlbum = async () => {
      if (selectedAlbum) {
         try {
            await unlinkAlbum(selectedAlbum.id);
            setAlbums(albums.filter((album) => album.id !== selectedAlbum.id));
            setUnlinkDialogOpen(false);
         } catch (error) {
            console.error('Error unlinking album:', error);
         }
      }
      handleMenuClose();
   };

   const handleCreateAlbum = async (albumData: {
      title: string;
      description: string;
      photoIds: string[];
      type?: 'private' | 'public' | 'paid';
      enableParticipantParticipation?: boolean;
      albumDate?: string;
   }) => {
      try {
         await createAlbumFromPhotos(albumData);
         setCreateAlbumOpen(false);

         loadAlbums(); // Refresh the list
      } catch (error) {
         console.error('Error creating album:', error);
      }
   };

   const handleViewAlbumPhotos = async (album: Album) => {
      setSelectedAlbum(album);
      setLoadingPhotos(true);
      setPhotoViewerOpen(true);

      try {
         // Fetch album photos
         const { getAuthHeaders } = await import('../../utils/auth');
         const headers = await getAuthHeaders();

         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/albums/photos/batch`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
               photoIds: album.photoIds || [],
               userId: userId,
            }),
         });

         if (response.ok) {
            const photosData = await response.json();
            setAlbumPhotos(photosData.photos || []);
         } else {
            console.error('Failed to fetch album photos');
            setAlbumPhotos([]);
         }
      } catch (error) {
         console.error('Error fetching album photos:', error);
         setAlbumPhotos([]);
      } finally {
         setLoadingPhotos(false);
      }
   };

   const handleEditAlbum = async (album: Album) => {
      setEditingAlbum(album);
      setEditTitle(album.title);
      setEditDescription(album.description || '');
      setSelectedCoverPhoto(album.coverPhotoId || null);

      // Load album photos for cover photo selection
      try {
         const { getAuthHeaders } = await import('../../utils/auth');
         const headers = await getAuthHeaders();

         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/albums/photos/batch`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ photoIds: album.photoIds || [] }),
         });

         if (response.ok) {
            const photosData = await response.json();
            setAlbumPhotos(photosData.photos || []);
         }
      } catch (error) {
         console.error('Error fetching album photos for edit:', error);
      }

      setEditDialogOpen(true);
   };

   const handleSaveEdit = async () => {
      if (!editingAlbum) return;

      try {
         const { getAuthHeaders } = await import('../../utils/auth');
         const headers = await getAuthHeaders();

         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${editingAlbum.id}`,
            {
               method: 'PUT',
               headers,
               body: JSON.stringify({
                  title: editTitle,
                  description: editDescription,
                  coverPhotoId: selectedCoverPhoto,
               }),
            }
         );

         if (response.ok) {
            // Refresh the albums list and cover photos
            await loadAlbums();
            setEditDialogOpen(false);
            setEditingAlbum(null);
         } else {
            console.error('Failed to update album');
         }
      } catch (error) {
         console.error('Error updating album:', error);
      }
   };

   const handleCancelEdit = () => {
      setEditDialogOpen(false);
      setEditingAlbum(null);
      setEditTitle('');
      setEditDescription('');
      setSelectedCoverPhoto(null);
   };

   const handleAddPhotos = async (album: Album) => {
      setAddPhotosAlbum(album);
      setAddPhotosAlbumId(album?.id ? String(album.id) : null);
      setSelectedTripPhotos(new Set());

      // In event context, use the photos prop (event photos); otherwise fetch trip photos
      if (eventId && photos && photos.length > 0) {
         const normalized = photos.map((p) => ({
            id: p.id,
            url: p.url || (p as any).thumbnailUrl || '',
            thumbnailUrl: (p as any).thumbnailUrl || p.url,
            name: (p as any).name ?? (p as any).title ?? 'Untitled',
            takenAt: (p as any).takenAt,
            size: (p as any).size,
            location: (p as any).location,
         }));
         setTripPhotos(normalized);
         setAddPhotosDialogOpen(true);
         return;
      }

      if (tripId) {
         try {
            const { getAuthHeaders } = await import('../../utils/auth');
            const headers = await getAuthHeaders();

            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/trips/${tripId}/photos`,
               {
                  headers,
               }
            );
            if (response.ok) {
               const photosData = await response.json();
               setTripPhotos(photosData.photos || []);
            } else {
               setTripPhotos([]);
            }
         } catch (error) {
            console.error('Error fetching trip photos:', error);
            setTripPhotos([]);
         }
      } else {
         setTripPhotos([]);
      }

      setAddPhotosDialogOpen(true);
   };

   // Helper function to check if a photo is already in the album
   const isPhotoInAlbum = (photoId: string) => {
      return addPhotosAlbum?.photoIds?.includes(photoId) || false;
   };

   // Get photos that are NOT already in the album
   const getAvailablePhotos = () => {
      return tripPhotos.filter((photo) => !isPhotoInAlbum(photo.id));
   };

   // Get photos that are already in the album
   const getAlreadyAddedPhotos = () => {
      return tripPhotos.filter((photo) => isPhotoInAlbum(photo.id));
   };

   // Handle opening full image viewer
   const handleOpenFullImage = (photoIndex: number) => {
      setCurrentImageIndex(photoIndex);
      setFullImageViewerOpen(true);
   };

   // Navigate to next image
   const handleNextImage = () => {
      setCurrentImageIndex((prev) => (prev + 1) % albumPhotos.length);
   };

   // Navigate to previous image
   const handlePrevImage = () => {
      setCurrentImageIndex((prev) => (prev - 1 + albumPhotos.length) % albumPhotos.length);
   };

   // Handle keyboard navigation
   const handleKeyPress = (event: React.KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
         handleNextImage();
      } else if (event.key === 'ArrowLeft') {
         handlePrevImage();
      } else if (event.key === 'Escape') {
         setFullImageViewerOpen(false);
      }
   };

   const handleToggleTripPhoto = (photoId: string) => {
      const newSelected = new Set(selectedTripPhotos);
      if (newSelected.has(photoId)) {
         newSelected.delete(photoId);
      } else {
         newSelected.add(photoId);
      }
      setSelectedTripPhotos(newSelected);
   };

   const closeAddPhotosModal = () => {
      setAddPhotosDialogOpen(false);
      setAddPhotosAlbum(null);
      setAddPhotosAlbumId(null);
      setSelectedTripPhotos(new Set());
      setPendingUploadFiles([]);
      setPendingPreviewUrls((urls) => {
         urls.forEach((u) => URL.revokeObjectURL(u));
         return [];
      });
   };

   useEffect(() => {
      if (pendingUploadFiles.length === 0) {
         setPendingPreviewUrls((urls) => {
            urls.forEach((u) => URL.revokeObjectURL(u));
            return [];
         });
         return;
      }
      const urls = pendingUploadFiles.map((f) => URL.createObjectURL(f));
      setPendingPreviewUrls((prev) => {
         prev.forEach((u) => URL.revokeObjectURL(u));
         return urls;
      });
      return () => {
         setPendingPreviewUrls((prev) => {
            prev.forEach((u) => URL.revokeObjectURL(u));
            return [];
         });
      };
   }, [pendingUploadFiles]);

   const handleAddSelectedPhotos = async () => {
      const albumId = addPhotosAlbumId ?? addPhotosAlbum?.id;
      if (!albumId || selectedTripPhotos.size === 0) return;

      try {
         const { getAuthHeaders } = await import('../../utils/auth');
         const headers = await getAuthHeaders();

         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/add-photos`,
            {
               method: 'POST',
               headers,
               body: JSON.stringify({
                  photoIds: Array.from(selectedTripPhotos),
               }),
            }
         );

         if (response.ok) {
            await loadAlbums();
            closeAddPhotosModal();
         } else {
            console.error('Failed to add photos to album');
         }
      } catch (error) {
         console.error('Error adding photos to album:', error);
      }
   };

   // File size validation function
   const validateFileSizes = (files: File[]) => {
      const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes
      const validFiles: File[] = [];
      const invalidFiles: { file: File; error: string }[] = [];

      files.forEach((file) => {
         if (file.size > MAX_FILE_SIZE) {
            invalidFiles.push({
               file,
               error: `File "${file.name}" is too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). Maximum size is 50MB.`,
            });
         } else {
            validFiles.push(file);
         }
      });

      return { validFiles, invalidFiles };
   };

   /** Store selected files for preview; upload runs when user clicks Add/Upload button. */
   const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files?.length) return;
      const fileArray = Array.from(files);
      const { validFiles, invalidFiles } = validateFileSizes(fileArray);
      if (invalidFiles.length > 0) {
         const errorMessages = invalidFiles.map((item) => item.error).join('\n');
         console.warn(`Some files are too large:\n${errorMessages}`);
      }
      if (validFiles.length > 0) {
         setPendingUploadFiles((prev) => [...prev, ...validFiles]);
      }
      event.target.value = '';
   };

   /** Upload the given files to the sub-album (same attachment method as create: album doc id, eventId on photo). */
   const doUploadFiles = async (files: File[]) => {
      const rawAlbumId = addPhotosAlbumId ?? addPhotosAlbum?.id;
      const albumId = rawAlbumId ? String(rawAlbumId) : null;
      if (!files.length || !albumId) return;
      if (eventId && albumId === eventId) {
         console.error('Add photos: album id must be the sub-album document id, not the event id.');
         return;
      }

      setUploadingPhotos(true);
      try {
         const formData = new FormData();
         files.forEach((file) => formData.append('photos', file));
         if (tripId) formData.append('tripId', tripId);
         if (eventId) formData.append('eventId', eventId);

         const { getAuthHeaders } = await import('../../utils/auth');
         const authHeaders = await getAuthHeaders();
         const { 'Content-Type': _, ...headersWithoutContentType } = authHeaders;

         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/upload-photos`,
            {
               method: 'POST',
               headers: headersWithoutContentType,
               body: formData,
            }
         );

         if (response.ok) {
            await loadAlbums();
            if (eventId) {
               const result = await response.clone().json();
               const newPhotos = (result.uploadedPhotos || []).map((p: any) => ({
                  id: p.id,
                  url: p.url || p.thumbnailUrl || '',
                  thumbnailUrl: p.thumbnailUrl || p.url,
                  name: p.title || p.fileName || 'Untitled',
                  takenAt: p.takenAt,
                  size: p.size,
                  location: p.location,
               }));
               setTripPhotos((prev) => [...newPhotos, ...prev]);
            } else if (tripId) {
               const tripPhotosResponse = await fetch(
                  `${process.env.REACT_APP_API_URL}/api/trips/${tripId}/photos`,
                  { headers: authHeaders }
               );
               if (tripPhotosResponse.ok) {
                  const photosData = await tripPhotosResponse.json();
                  setTripPhotos(photosData.photos || []);
               }
            }
            setPendingUploadFiles((prev) => prev.filter((f) => !files.includes(f)));
            closeAddPhotosModal();
         } else {
            console.error('Failed to upload photos');
         }
      } catch (error) {
         console.error('Error uploading photos:', error);
      } finally {
         setUploadingPhotos(false);
      }
   };

   const handleAddPhotosSubmit = async () => {
      if (pendingUploadFiles.length > 0) {
         await doUploadFiles(pendingUploadFiles);
      }
   };

   const formatDate = (date: any) => {
      if (!date) return null;
      try {
         const dateObj = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
         // Check if the date is valid
         if (isNaN(dateObj.getTime())) {
            return null;
         }
         return dateObj.toLocaleDateString();
      } catch {
         return null;
      }
   };

   const getSourceIcon = (source?: string) => {
      switch (source) {
         case 'trip':
            return <TripOriginIcon fontSize='small' />;
         case 'event':
            return <EventIcon fontSize='small' />;
         default:
            return <PhotoLibraryIcon fontSize='small' />;
      }
   };

   const getSourceColor = (source?: string) => {
      switch (source) {
         case 'trip':
            return 'primary';
         case 'event':
            return 'secondary';
         default:
            return 'default';
      }
   };

   if (loading) {
      return (
         <Box>
            <Typography variant='h5' gutterBottom>
               {title || 'Linked Albums'}
            </Typography>
            <Grid container spacing={3}>
               {[1, 2, 3].map((item) => (
                  <Grid item xs={12} sm={6} md={4} key={item}>
                     <Card>
                        <Skeleton variant='rectangular' height={200} />
                        <CardContent>
                           <Skeleton variant='text' height={32} />
                           <Skeleton variant='text' height={20} />
                           <Skeleton variant='text' height={20} width='60%' />
                        </CardContent>
                     </Card>
                  </Grid>
               ))}
            </Grid>
         </Box>
      );
   }

   // Debug logging
   console.log('🎨 LinkedAlbums: Rendering with albums:', albums.length, albums);

   return (
      <Box>
         <Box display='flex' justifyContent='space-between' alignItems='center' sx={{ mb: 3 }}>
            <Typography variant='h5'>{title || 'Linked Albums'}</Typography>
            <Button
               variant='contained'
               startIcon={<AddIcon />}
               onClick={() => setCreateAlbumOpen(true)}
            >
               Create Album
            </Button>
         </Box>

         {error && (
            <Alert severity='error' sx={{ mb: 3 }}>
               {error}
            </Alert>
         )}

         {albums.length === 0 ? (
            <Box
               display='flex'
               flexDirection='column'
               alignItems='center'
               justifyContent='center'
               sx={{ py: 8, textAlign: 'center' }}
            >
               <CollectionsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
               <Typography variant='h6' color='text.secondary' gutterBottom>
                  No albums linked yet
               </Typography>
               <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
                  Create your first album {photos.length > 0 ? `from ${tripId ? 'trip' : 'event'} photos` : `for this ${tripId ? 'trip' : 'event'}`}
               </Typography>
               <Button
                  variant='contained'
                  startIcon={<AddIcon />}
                  onClick={() => setCreateAlbumOpen(true)}
               >
                  Create First Album
               </Button>
            </Box>
         ) : (
            <Grid container spacing={2}>
               {albums.map((album) => {
                  const coverPhotoUrl = getCoverPhotoUrl(album);

                  return (
                     <Grid item xs={12} sm={6} md={4} lg={3} key={album.id}>
                        <AlbumCard>
                           {/* Cover Image - using same pattern as AlbumsListPage */}
                           {coverPhotoUrl ? (
                              <CardMedia
                                 component='img'
                                 height='140'
                                 image={coverPhotoUrl}
                                 alt={album.title}
                                 sx={{ position: 'relative' }}
                              />
                           ) : (
                              <Box
                                 sx={{
                                    height: 140,
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: `linear-gradient(45deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light})`,
                                 }}
                              >
                                 <CollectionsIcon
                                    sx={{ fontSize: 32, color: 'white', opacity: 0.7 }}
                                 />
                              </Box>
                           )}

                           {/* Photo Count - positioned absolutely over the image */}
                           <PhotoCount
                              label={`${album.photoCount ?? album.photoIds?.length ?? 0} photos`}
                              size='small'
                              sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}
                           />

                           <CardContent sx={{ flexGrow: 1, py: 1.5, px: 2 }}>
                              <Box
                                 display='flex'
                                 justifyContent='space-between'
                                 alignItems='flex-start'
                                 sx={{ mb: 1 }}
                              >
                                 <Typography
                                    variant='subtitle1'
                                    component='h3'
                                    noWrap
                                    sx={{ flex: 1, mr: 1, fontWeight: 600 }}
                                 >
                                    {album.title}
                                 </Typography>
                              </Box>

                              {album.description && (
                                 <Typography
                                    variant='body2'
                                    color='text.secondary'
                                    sx={{
                                       mb: 1,
                                       display: '-webkit-box',
                                       WebkitLineClamp: 1,
                                       WebkitBoxOrient: 'vertical',
                                       overflow: 'hidden',
                                       fontSize: '0.8rem',
                                    }}
                                 >
                                    {album.description}
                                 </Typography>
                              )}

                              <Box display='flex' gap={0.5} flexWrap='wrap' sx={{ mb: 1 }}>
                                 {album.isPublic && (
                                    <Chip
                                       label='Public'
                                       size='small'
                                       color='success'
                                       variant='outlined'
                                       sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                 )}
                                 {album.members && album.members.length > 1 && (
                                    <Chip
                                       label={`${album.members.length} members`}
                                       size='small'
                                       color='primary'
                                       variant='outlined'
                                       sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                 )}
                                 {album.userRole && (
                                    <Chip
                                       label={album.userRole}
                                       size='small'
                                       color={
                                          album.userRole === 'owner'
                                             ? 'error'
                                             : album.userRole === 'editor'
                                               ? 'warning'
                                               : 'default'
                                       }
                                       variant='outlined'
                                       sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                 )}
                              </Box>

                              {formatDate(album.createdAt) && (
                                 <Typography
                                    variant='caption'
                                    color='text.secondary'
                                    sx={{ fontSize: '0.7rem' }}
                                 >
                                    Created {formatDate(album.createdAt)}
                                 </Typography>
                              )}
                           </CardContent>

                           <CardActions sx={{ py: 0.5, px: 1.5, justifyContent: 'space-between' }}>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                 <Button
                                    size='small'
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => navigate(`/albums/${album.id}`)}
                                    sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                                 >
                                    View
                                 </Button>
                                 <Button
                                    size='small'
                                    startIcon={<PhotoLibraryIcon />}
                                    onClick={() => handleViewAlbumPhotos(album)}
                                    sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                                 >
                                    Photos
                                 </Button>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                 <Button
                                    size='small'
                                    startIcon={<AddPhotoAlternateIcon />}
                                    onClick={() => handleAddPhotos(album)}
                                    variant='contained'
                                    color='primary'
                                    sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                                 >
                                    Add
                                 </Button>
                                 <IconButton
                                    size='small'
                                    onClick={() => {
                                       // Handle share functionality
                                       navigator.share?.({
                                          title: album.title,
                                          url: `${window.location.origin}/albums/${album.id}`,
                                       });
                                    }}
                                    sx={{ p: 0.5 }}
                                 >
                                    <ShareIcon fontSize='small' />
                                 </IconButton>
                              </Box>
                           </CardActions>
                        </AlbumCard>
                     </Grid>
                  );
               })}
            </Grid>
         )}

         {/* Context Menu */}
         <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem onClick={handleViewAlbum}>
               <VisibilityIcon sx={{ mr: 1 }} />
               View Album
            </MenuItem>
            <MenuItem
               onClick={() => {
                  if (selectedAlbum) handleEditAlbum(selectedAlbum);
                  handleMenuClose();
               }}
            >
               <EditIcon sx={{ mr: 1 }} />
               Edit Album
            </MenuItem>
            <MenuItem onClick={() => setUnlinkDialogOpen(true)}>
               <LinkOffIcon sx={{ mr: 1 }} />
               Unlink Album
            </MenuItem>
         </Menu>

         {/* Unlink Confirmation Dialog */}
         <Dialog open={unlinkDialogOpen} onClose={() => setUnlinkDialogOpen(false)}>
            <DialogTitle>Unlink Album</DialogTitle>
            <DialogContent>
               <Typography>
                  Are you sure you want to unlink "{selectedAlbum?.title}" from this{' '}
                  {tripId ? 'trip' : 'event'}? The album will still exist but won't be associated
                  with this {tripId ? 'trip' : 'event'}.
               </Typography>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setUnlinkDialogOpen(false)}>Cancel</Button>
               <Button onClick={handleUnlinkAlbum} color='error' disabled={actionLoading}>
                  Unlink
               </Button>
            </DialogActions>
         </Dialog>

         {/* Create Album Dialog */}
         <CreateAlbumFromPhotos
            open={createAlbumOpen}
            onClose={() => setCreateAlbumOpen(false)}
            photos={photos}
            onCreateAlbum={handleCreateAlbum}
            tripId={tripId}
            eventId={eventId}
         />

         {/* Photo Viewer Modal */}
         <Dialog
            open={photoViewerOpen}
            onClose={() => setPhotoViewerOpen(false)}
            maxWidth='xl'
            fullWidth
            PaperProps={{
               sx: {
                  height: '95vh',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
               },
            }}
         >
            <DialogTitle sx={{ pb: 1 }}>
               <Box display='flex' justifyContent='space-between' alignItems='center'>
                  <Box>
                     <Typography variant='h5' component='h2' sx={{ fontWeight: 600, mb: 0.5 }}>
                        {selectedAlbum?.title}
                     </Typography>
                     <Box display='flex' alignItems='center' gap={2}>
                        <Chip
                           icon={<PhotoLibraryIcon />}
                           label={`${albumPhotos.length} photo${albumPhotos.length !== 1 ? 's' : ''}`}
                           size='small'
                           variant='outlined'
                        />
                        {selectedAlbum?.description && (
                           <Typography
                              variant='body2'
                              color='text.secondary'
                              sx={{ maxWidth: 400 }}
                           >
                              {selectedAlbum.description}
                           </Typography>
                        )}
                     </Box>
                  </Box>
                  <Box display='flex' alignItems='center' gap={1}>
                     <Tooltip title='Download All Photos'>
                        <IconButton
                           onClick={() => {
                              // TODO: Implement bulk download functionality
                              console.log('Download all photos');
                           }}
                           size='small'
                        >
                           <DownloadIcon />
                        </IconButton>
                     </Tooltip>
                     <Tooltip title='Share Album'>
                        <IconButton
                           onClick={() => {
                              navigator.share?.({
                                 title: selectedAlbum?.title,
                                 url: `${window.location.origin}/albums/${selectedAlbum?.id}`,
                              });
                           }}
                           size='small'
                        >
                           <ShareIcon />
                        </IconButton>
                     </Tooltip>
                     <IconButton onClick={() => setPhotoViewerOpen(false)} size='small'>
                        <CloseIcon />
                     </IconButton>
                  </Box>
               </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 2, height: '100%', overflow: 'hidden' }}>
               {loadingPhotos ? (
                  <Box
                     display='flex'
                     flexDirection='column'
                     justifyContent='center'
                     alignItems='center'
                     sx={{ height: '100%' }}
                  >
                     <CircularProgress size={48} sx={{ mb: 2 }} />
                     <Typography variant='body1' color='text.secondary'>
                        Loading photos...
                     </Typography>
                  </Box>
               ) : albumPhotos.length === 0 ? (
                  <Box
                     display='flex'
                     flexDirection='column'
                     alignItems='center'
                     justifyContent='center'
                     sx={{ height: '100%' }}
                  >
                     <PhotoLibraryIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 3 }} />
                     <Typography variant='h6' color='text.secondary' gutterBottom>
                        No photos in this album
                     </Typography>
                     <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ mb: 3, textAlign: 'center' }}
                     >
                        Add some photos to this album to see them here
                     </Typography>
                     {selectedAlbum?.userRole === 'owner' && (
                        <Button
                           variant='contained'
                           startIcon={<AddPhotoAlternateIcon />}
                           onClick={() => {
                              setPhotoViewerOpen(false);
                              if (selectedAlbum) handleAddPhotos(selectedAlbum);
                           }}
                        >
                           Add Photos
                        </Button>
                     )}
                  </Box>
               ) : (
                  <Box sx={{ height: '100%', overflow: 'auto' }}>
                     <Grid container spacing={2}>
                        {albumPhotos.map((photo, index) => (
                           <Grid item xs={12} sm={6} md={4} lg={3} key={photo.id}>
                              <Card
                                 sx={{
                                    position: 'relative',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                       transform: 'translateY(-4px)',
                                       boxShadow: theme.shadows[8],
                                    },
                                 }}
                                 onClick={() => handleOpenFullImage(index)}
                              >
                                 <CardMedia
                                    component='img'
                                    height='200'
                                    image={photo.thumbnailUrl || photo.url}
                                    alt={photo.name || `Photo ${index + 1}`}
                                    sx={{
                                       objectFit: 'cover',
                                       transition: 'transform 0.3s ease',
                                       '&:hover': {
                                          transform: 'scale(1.02)',
                                       },
                                    }}
                                 />
                                 <Box
                                    sx={{
                                       position: 'absolute',
                                       top: 8,
                                       right: 8,
                                       display: 'flex',
                                       gap: 0.5,
                                    }}
                                 >
                                    <Tooltip title='View Full Size'>
                                       <IconButton
                                          size='small'
                                          sx={{
                                             bgcolor: 'rgba(0,0,0,0.6)',
                                             color: 'white',
                                             '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                                          }}
                                          onClick={(e) => {
                                             e.stopPropagation();
                                             window.open(photo.url, '_blank');
                                          }}
                                       >
                                          <FullscreenIcon fontSize='small' />
                                       </IconButton>
                                    </Tooltip>
                                    <Tooltip title='Download'>
                                       <IconButton
                                          size='small'
                                          sx={{
                                             bgcolor: 'rgba(0,0,0,0.6)',
                                             color: 'white',
                                             '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                                          }}
                                          onClick={(e) => {
                                             e.stopPropagation();
                                             // Create download link
                                             const link = document.createElement('a');
                                             link.href = photo.url;
                                             link.download = photo.name || `photo-${index + 1}.jpg`;
                                             link.click();
                                          }}
                                       >
                                          <DownloadIcon fontSize='small' />
                                       </IconButton>
                                    </Tooltip>
                                 </Box>
                                 <CardContent sx={{ p: 1.5, pb: '12px !important' }}>
                                    <Typography
                                       variant='body2'
                                       sx={{
                                          fontWeight: 500,
                                          mb: 0.5,
                                          display: '-webkit-box',
                                          WebkitLineClamp: 1,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                       }}
                                    >
                                       {photo.name || `Photo ${index + 1}`}
                                    </Typography>
                                    <Typography variant='caption' color='text.secondary'>
                                       {photo.takenAt
                                          ? new Date(
                                               photo.takenAt.seconds * 1000
                                            ).toLocaleDateString()
                                          : 'Unknown date'}
                                    </Typography>
                                 </CardContent>
                              </Card>
                           </Grid>
                        ))}
                     </Grid>
                  </Box>
               )}
            </DialogContent>
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
               <Box display='flex' justifyContent='space-between' alignItems='center'>
                  <Typography variant='body2' color='text.secondary'>
                     {albumPhotos.length > 0 &&
                        `Showing ${albumPhotos.length} photo${albumPhotos.length !== 1 ? 's' : ''}`}
                  </Typography>
                  <Box display='flex' gap={1}>
                     <Button
                        variant='outlined'
                        startIcon={<AddPhotoAlternateIcon />}
                        onClick={() => {
                           setPhotoViewerOpen(false);
                           if (selectedAlbum) handleAddPhotos(selectedAlbum);
                        }}
                        size='small'
                     >
                        Add More Photos
                     </Button>
                     <Button
                        variant='contained'
                        onClick={() => setPhotoViewerOpen(false)}
                        size='small'
                     >
                        Close
                     </Button>
                  </Box>
               </Box>
            </Box>
         </Dialog>

         {/* Edit Album Dialog */}
         <Dialog open={editDialogOpen} onClose={handleCancelEdit} maxWidth='md' fullWidth>
            <DialogTitle>
               <Box display='flex' justifyContent='space-between' alignItems='center'>
                  <Typography variant='h6'>Edit Album</Typography>
                  <IconButton onClick={handleCancelEdit}>
                     <CloseIcon />
                  </IconButton>
               </Box>
            </DialogTitle>
            <DialogContent>
               <Box sx={{ pt: 2 }}>
                  <TextField
                     fullWidth
                     label='Album Title'
                     value={editTitle}
                     onChange={(e) => setEditTitle(e.target.value)}
                     sx={{ mb: 3 }}
                  />
                  <TextField
                     fullWidth
                     label='Description'
                     value={editDescription}
                     onChange={(e) => setEditDescription(e.target.value)}
                     multiline
                     rows={3}
                     sx={{ mb: 3 }}
                  />

                  {albumPhotos.length > 0 && (
                     <Box>
                        <Typography variant='h6' sx={{ mb: 2 }}>
                           Select Cover Photo
                        </Typography>
                        <Grid container spacing={2}>
                           {albumPhotos.map((photo) => (
                              <Grid item xs={6} sm={4} md={3} key={photo.id}>
                                 <Card
                                    sx={{
                                       cursor: 'pointer',
                                       border: selectedCoverPhoto === photo.id ? 2 : 0,
                                       borderColor: 'primary.main',
                                       transition: 'all 0.2s',
                                    }}
                                    onClick={() => setSelectedCoverPhoto(photo.id)}
                                 >
                                    <CardMedia
                                       component='img'
                                       height='120'
                                       image={photo.thumbnailUrl || photo.url}
                                       alt={photo.name}
                                       sx={{ objectFit: 'cover' }}
                                    />
                                    <CardContent sx={{ p: 1 }}>
                                       <Typography variant='caption' noWrap>
                                          {photo.name}
                                       </Typography>
                                    </CardContent>
                                 </Card>
                              </Grid>
                           ))}
                        </Grid>
                     </Box>
                  )}
               </Box>
            </DialogContent>
            <DialogActions>
               <Button onClick={handleCancelEdit}>Cancel</Button>
               <Button onClick={handleSaveEdit} variant='contained' disabled={!editTitle.trim()}>
                  Save Changes
               </Button>
            </DialogActions>
         </Dialog>

         {/* Add Photos Dialog */}
         <Dialog
            open={addPhotosDialogOpen}
            onClose={closeAddPhotosModal}
            maxWidth='md'
            fullWidth
            PaperProps={{
               sx: { height: '80vh' },
            }}
         >
            <DialogTitle>
               <Box display='flex' justifyContent='space-between' alignItems='center'>
                  <Typography variant='h6'>Add Photos to "{addPhotosAlbum?.title}"</Typography>
                  <IconButton onClick={closeAddPhotosModal}>
                     <CloseIcon />
                  </IconButton>
               </Box>
            </DialogTitle>
            <DialogContent>
               <Box sx={{ pt: 2 }}>
                  {addPhotosAlbum?.userRole === 'owner' && (
                     <Card sx={{ p: 2 }}>
                        <input
                           accept='image/*'
                           style={{ display: 'none' }}
                           id='photo-upload-input'
                           multiple
                           type='file'
                           onChange={handleFileSelect}
                        />
                        <label htmlFor='photo-upload-input'>
                           <Button
                              variant='outlined'
                              component='span'
                              startIcon={<CloudUploadIcon />}
                              disabled={uploadingPhotos}
                              size='large'
                              sx={{ width: '100%', py: 2 }}
                           >
                              Choose Photos to Upload
                           </Button>
                        </label>

                        {pendingUploadFiles.length > 0 ? (
                           <>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
                                 <Typography variant='subtitle1'>
                                    {pendingUploadFiles.length} photo{pendingUploadFiles.length !== 1 ? 's' : ''} selected
                                 </Typography>
                                 <Button
                                    size='small'
                                    onClick={() => setPendingUploadFiles([])}
                                    disabled={uploadingPhotos}
                                 >
                                    Clear
                                 </Button>
                              </Box>
                              <ImageList
                                 cols={isMobile ? 2 : 4}
                                 gap={8}
                                 sx={{ maxHeight: 360, overflow: 'auto' }}
                              >
                                 {pendingPreviewUrls.map((url, i) => (
                                    <ImageListItem key={i}>
                                       <Box
                                          component='img'
                                          src={url}
                                          alt={pendingUploadFiles[i]?.name || 'Preview'}
                                          sx={{
                                             width: '100%',
                                             height: 140,
                                             objectFit: 'cover',
                                             borderRadius: 1,
                                             border: '1px solid',
                                             borderColor: 'divider',
                                          }}
                                       />
                                       <Typography variant='caption' sx={{ mt: 0.5, display: 'block' }} noWrap>
                                          {pendingUploadFiles[i]?.name}
                                       </Typography>
                                    </ImageListItem>
                                 ))}
                              </ImageList>
                           </>
                        ) : (
                           <Box
                              display='flex'
                              flexDirection='column'
                              alignItems='center'
                              sx={{ py: 4 }}
                           >
                              <PhotoLibraryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                              <Typography variant='body2' color='text.secondary'>
                                 Choose photos above to preview, then click Add to upload to this album
                              </Typography>
                           </Box>
                        )}

                        {uploadingPhotos && (
                           <Box sx={{ mt: 2 }}>
                              <LinearProgress />
                           </Box>
                        )}
                     </Card>
                  )}
               </Box>
            </DialogContent>
            <DialogActions>
               <Button onClick={closeAddPhotosModal}>Cancel</Button>
               <Button
                  onClick={handleAddPhotosSubmit}
                  variant='contained'
                  disabled={pendingUploadFiles.length === 0 || uploadingPhotos}
                  startIcon={<AddPhotoAlternateIcon />}
               >
                  Add {pendingUploadFiles.length} photo{pendingUploadFiles.length !== 1 ? 's' : ''}
               </Button>
            </DialogActions>
         </Dialog>

         {/* Full Image Viewer */}
         <Dialog
            open={fullImageViewerOpen}
            onClose={() => setFullImageViewerOpen(false)}
            maxWidth={false}
            fullScreen
            PaperProps={{
               sx: {
                  bgcolor: 'rgba(0, 0, 0, 0.95)',
                  backdropFilter: 'blur(10px)',
               },
            }}
            onKeyDown={handleKeyPress}
            tabIndex={-1}
         >
            <Box
               sx={{
                  position: 'relative',
                  width: '100vw',
                  height: '100vh',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
               }}
            >
               {/* Close Button */}
               <IconButton
                  onClick={() => setFullImageViewerOpen(false)}
                  sx={{
                     position: 'absolute',
                     top: 16,
                     right: 16,
                     zIndex: 1000,
                     bgcolor: 'rgba(0, 0, 0, 0.7)',
                     color: 'white',
                     '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.9)',
                     },
                  }}
               >
                  <CloseIcon />
               </IconButton>

               {/* Image Info */}
               <Box
                  sx={{
                     position: 'absolute',
                     top: 16,
                     left: 16,
                     zIndex: 1000,
                     bgcolor: 'rgba(0, 0, 0, 0.7)',
                     color: 'white',
                     px: 2,
                     py: 1,
                     borderRadius: 1,
                  }}
               >
                  <Typography variant='h6' sx={{ mb: 0.5 }}>
                     {albumPhotos[currentImageIndex]?.name || `Photo ${currentImageIndex + 1}`}
                  </Typography>
                  <Typography variant='body2' sx={{ opacity: 0.8 }}>
                     {currentImageIndex + 1} of {albumPhotos.length}
                  </Typography>
               </Box>

               {/* Previous Button */}
               {albumPhotos.length > 1 && (
                  <IconButton
                     onClick={handlePrevImage}
                     sx={{
                        position: 'absolute',
                        left: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 1000,
                        bgcolor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        width: 56,
                        height: 56,
                        '&:hover': {
                           bgcolor: 'rgba(0, 0, 0, 0.9)',
                        },
                     }}
                  >
                     <ArrowBackIcon />
                  </IconButton>
               )}

               {/* Next Button */}
               {albumPhotos.length > 1 && (
                  <IconButton
                     onClick={handleNextImage}
                     sx={{
                        position: 'absolute',
                        right: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 1000,
                        bgcolor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        width: 56,
                        height: 56,
                        '&:hover': {
                           bgcolor: 'rgba(0, 0, 0, 0.9)',
                        },
                     }}
                  >
                     <ArrowForwardIcon />
                  </IconButton>
               )}

               {/* Main Image */}
               {albumPhotos[currentImageIndex] && (
                  <img
                     src={albumPhotos[currentImageIndex].url}
                     alt={albumPhotos[currentImageIndex].name || `Photo ${currentImageIndex + 1}`}
                     style={{
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                     }}
                     onClick={(e) => e.stopPropagation()}
                  />
               )}

               {/* Bottom Actions */}
               <Box
                  sx={{
                     position: 'absolute',
                     bottom: 16,
                     left: '50%',
                     transform: 'translateX(-50%)',
                     zIndex: 1000,
                     display: 'flex',
                     gap: 1,
                     bgcolor: 'rgba(0, 0, 0, 0.7)',
                     borderRadius: 2,
                     p: 1,
                  }}
               >
                  <Tooltip title='Download'>
                     <IconButton
                        onClick={() => {
                           const link = document.createElement('a');
                           link.href = albumPhotos[currentImageIndex].url;
                           link.download =
                              albumPhotos[currentImageIndex].name ||
                              `photo-${currentImageIndex + 1}.jpg`;
                           link.click();
                        }}
                        sx={{ color: 'white' }}
                     >
                        <DownloadIcon />
                     </IconButton>
                  </Tooltip>
                  <Tooltip title='Open in New Tab'>
                     <IconButton
                        onClick={() => window.open(albumPhotos[currentImageIndex].url, '_blank')}
                        sx={{ color: 'white' }}
                     >
                        <FullscreenIcon />
                     </IconButton>
                  </Tooltip>
                  <Tooltip title='Share'>
                     <IconButton
                        onClick={() => {
                           navigator.share?.({
                              title: albumPhotos[currentImageIndex].name || 'Photo',
                              url: albumPhotos[currentImageIndex].url,
                           });
                        }}
                        sx={{ color: 'white' }}
                     >
                        <ShareIcon />
                     </IconButton>
                  </Tooltip>
               </Box>

               {/* Thumbnail Strip (for navigation) */}
               {albumPhotos.length > 1 && (
                  <Box
                     sx={{
                        position: 'absolute',
                        bottom: 80,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: 1,
                        maxWidth: '80vw',
                        overflow: 'auto',
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        borderRadius: 2,
                        p: 1,
                     }}
                  >
                     {albumPhotos.map((photo, index) => (
                        <Box
                           key={photo.id}
                           onClick={() => setCurrentImageIndex(index)}
                           sx={{
                              width: 60,
                              height: 40,
                              cursor: 'pointer',
                              border:
                                 index === currentImageIndex
                                    ? '2px solid white'
                                    : '2px solid transparent',
                              borderRadius: 1,
                              overflow: 'hidden',
                              opacity: index === currentImageIndex ? 1 : 0.6,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                 opacity: 1,
                                 border: '2px solid rgba(255, 255, 255, 0.7)',
                              },
                           }}
                        >
                           <img
                              src={photo.thumbnailUrl || photo.url}
                              alt={photo.name || `Photo ${index + 1}`}
                              style={{
                                 width: '100%',
                                 height: '100%',
                                 objectFit: 'cover',
                              }}
                           />
                        </Box>
                     ))}
                  </Box>
               )}
            </Box>
         </Dialog>

         {/* Floating Action Button for Mobile */}
         <CreateAlbumFab
            color='primary'
            onClick={() => setCreateAlbumOpen(true)}
            sx={{ display: { xs: 'flex', md: 'none' } }}
         >
            <AddIcon />
         </CreateAlbumFab>
      </Box>
   );
};

export default LinkedAlbums; 